import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';

const MAX_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
];
const ALLOWED_EXTENSIONS = /\.(pdf|jpg|jpeg|png|webp)$/i;

type DropzoneState = 'idle' | 'dragover' | 'success' | 'error';

@Component({
  standalone: false,
  selector: 'digicem-receipt-dropzone',
  templateUrl: './receipt-dropzone.component.html',
})
export class ReceiptDropzoneComponent {
  @Input() set serverError(value: boolean) {
    if (value && this.state === 'success') {
      this.state = 'error';
    }
  }

  @Output() fileSelected = new EventEmitter<File | null>();

  protected state: DropzoneState = 'idle';
  protected selectedFile: File | null = null;

  protected get fileSizeMB(): string {
    return this.selectedFile
      ? (this.selectedFile.size / (1024 * 1024)).toFixed(1) + ' MB'
      : '';
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (this.state !== 'success' && this.state !== 'error') {
      this.state = 'dragover';
    }
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    if (this.state === 'dragover') {
      this.state = 'idle';
    }
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0] ?? null;
    this.processFile(file);
  }

  protected onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.processFile(file);
    input.value = '';
  }

  protected removeFile(): void {
    this.selectedFile = null;
    this.state = 'idle';
    this.fileSelected.emit(null);
  }

  private processFile(file: File | null): void {
    if (!file) return;

    this.selectedFile = file;

    if (
      !this.isValidType(file) ||
      file.size === 0 ||
      file.size > MAX_SIZE_BYTES
    ) {
      this.state = 'error';
      this.fileSelected.emit(null);
      return;
    }

    this.state = 'success';
    this.fileSelected.emit(file);
  }

  private isValidType(file: File): boolean {
    return (
      ALLOWED_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.test(file.name)
    );
  }
}
